import React from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

/**
 * TagSelect — a generic multi-select "tag" autocomplete.
 *
 * A thin wrapper around MUI's `Autocomplete` configured for the common
 * "pick several items, shown as removable chips" pattern, with free typing
 * (`freeSolo`) on by default so users can add values that aren't in the
 * suggestion list. The freeSolo `string` case is handled internally, so the
 * `getKey` / `getOptionLabel` / `getChipLabel` / `renderChipIcon` callbacks
 * you pass only ever deal with your option type `T`.
 *
 * Use this for any chip-style multi-select (attendees, tags, recipients,
 * labels, …). For a single-value dropdown, use the `AutoComplete` component
 * instead — it is single-select and keyed by numeric id.
 *
 * `onChange` returns `(T | string)[]`: real options plus any raw strings the
 * user typed. Normalise/validate those in the parent (e.g. dedupe, trim,
 * reject invalid entries).
 */
export interface TagSelectProps<T> {
  /** Currently selected items, rendered as chips. */
  value: T[];
  /** Suggestions shown in the dropdown. */
  options: T[];
  /** Fired with the new selection. With freeSolo on, items may be raw strings. */
  onChange: (items: (T | string)[]) => void;
  /** Stable identity for an item — used to dedupe and to match value↔option. */
  getKey: (item: T) => string;
  /** Text shown for an item in the dropdown list. */
  getOptionLabel: (item: T) => string;
  /** Text shown on the selected chip. Defaults to `getOptionLabel`. */
  getChipLabel?: (item: T) => string;
  /** Optional leading icon rendered inside a chip. */
  renderChipIcon?: (item: T) => React.ReactElement | undefined;
  label?: string;
  /** Shown only while nothing is selected. */
  placeholder?: string;
  size?: "small" | "medium";
  /** Allow arbitrary typed values, not just options. Default `true`. */
  freeSolo?: boolean;
}

function TagSelect<T>({
  value,
  options,
  onChange,
  getKey,
  getOptionLabel,
  getChipLabel,
  renderChipIcon,
  label,
  placeholder,
  size = "small",
  freeSolo = true,
}: TagSelectProps<T>) {
  const keyOf = (item: T | string): string =>
    typeof item === "string" ? item.trim() : getKey(item);

  const chipLabelOf = (item: T | string): string =>
    typeof item === "string" ? item : (getChipLabel ?? getOptionLabel)(item);

  return (
    <Autocomplete<T, true, false, boolean>
      multiple
      freeSolo={freeSolo}
      size={size}
      options={options}
      value={value}
      filterSelectedOptions
      getOptionLabel={(item) =>
        typeof item === "string" ? item : getOptionLabel(item)
      }
      isOptionEqualToValue={(option, selected) =>
        keyOf(option) === keyOf(selected)
      }
      onChange={(_, newValue) => onChange(newValue)}
      renderTags={(items, getTagProps) =>
        items.map((item, index) => {
          const { key, ...tagProps } = getTagProps({ index });
          return (
            <Chip
              key={key}
              size="small"
              label={chipLabelOf(item)}
              icon={
                typeof item === "string" ? undefined : renderChipIcon?.(item)
              }
              {...tagProps}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={value.length === 0 ? placeholder : undefined}
          inputProps={{ ...params.inputProps, autoComplete: "new-password" }}
        />
      )}
    />
  );
}

export default TagSelect;
